/* ============================================================
   Miorenée Currency Converter
   ------------------------------------------------------------
   Display-only conversion for stores without Shopify Markets /
   Shopify Payments. Checkout always charges the store currency;
   converted prices are estimates (see the cart note).

   How it works
   - Reads shop money format + base currency from window.MioConfig.
   - Builds a regex from the money format and converts matching
     amounts inside text nodes (WeakMap keeps originals, so
     switching back restores exact server-rendered prices).
   - A MutationObserver re-converts anything the theme re-renders
     (AJAX carts, facet grids, variant-change price rewrites,
     infinite scroll, the wishlist grid...).
   - Rates: fawazahmed0 currency-api (CDN, free, daily) with two
     fallbacks; cached in localStorage for 12 h.
   - First visit: detects country via Shopify's own
     /browsing_context_suggestions.json and picks a currency.

   Events (on document):
     mio:currency:change  detail: { currency, base, rate }
   ============================================================ */
(function () {
  'use strict';
  if (window.MioCurrency) return;

  var CFG = (window.MioConfig && window.MioConfig.currency) || {};
  var BASE = (CFG.base || 'USD').toUpperCase();
  var LIST = (CFG.list || 'USD,EUR,GBP,INR,AED,CAD,AUD,SGD,SAR,JPY')
    .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
  if (LIST.indexOf(BASE) === -1) LIST.unshift(BASE);
  var AUTO = CFG.autoDetect !== false;
  var PREF_KEY = 'mio_currency_pref';
  var RATES_KEY = 'mio_currency_rates_v1';
  var GEO_KEY = 'mio_geo_country';
  var RATES_TTL = 12 * 60 * 60 * 1000;

  var NAMES = {
    USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', INR: 'Indian Rupee',
    AED: 'UAE Dirham', CAD: 'Canadian Dollar', AUD: 'Australian Dollar',
    SGD: 'Singapore Dollar', SAR: 'Saudi Riyal', JPY: 'Japanese Yen',
    NZD: 'NZ Dollar', CHF: 'Swiss Franc', HKD: 'Hong Kong Dollar', QAR: 'Qatari Riyal',
    KWD: 'Kuwaiti Dinar', MYR: 'Malaysian Ringgit', THB: 'Thai Baht', ZAR: 'South African Rand'
  };
  var SYMBOLS = {
    USD: '$', EUR: '€', GBP: '£', INR: '₹', AED: 'AED', CAD: 'CA$',
    AUD: 'A$', SGD: 'S$', SAR: 'SAR', JPY: '¥', NZD: 'NZ$', CHF: 'CHF',
    HKD: 'HK$', QAR: 'QAR', KWD: 'KWD', MYR: 'RM', THB: '฿', ZAR: 'R'
  };
  var LOCALES = { INR: 'en-IN', EUR: 'en-IE' };

  var EU = ['AT','BE','CY','DE','EE','ES','FI','FR','GR','HR','IE','IT','LT','LU','LV','MC','MT','NL','PT','SI','SK','SM'];
  function countryToCurrency(cc) {
    if (!cc) return null;
    cc = cc.toUpperCase();
    var map = {
      US: 'USD', IN: 'INR', GB: 'GBP', AE: 'AED', SA: 'SAR', CA: 'CAD',
      AU: 'AUD', SG: 'SGD', JP: 'JPY', NZ: 'NZD', CH: 'CHF', HK: 'HKD',
      QA: 'QAR', KW: 'KWD', MY: 'MYR', TH: 'THB', ZA: 'ZAR'
    };
    if (EU.indexOf(cc) !== -1) return 'EUR';
    var cur = map[cc];
    return (cur && LIST.indexOf(cur) !== -1) ? cur : null;
  }

  /* ---------- state ---------- */
  var state = {
    active: BASE,
    rates: null,            /* { EUR: 0.92, ... } relative to BASE */
    records: new WeakMap(), /* Text node -> { orig, out } */
    tracked: []             /* array of WeakRef-ish { node } for restore */
  };
  var applying = false;

  /* ---------- money regexes from shop formats ---------- */
  function escapeReg(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/(\s| )+/g, '[\\s\\u00a0]*');
  }
  function parseFormat(fmt) {
    fmt = String(fmt || '').replace(/<[^>]+>/g, '');
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var kind = m ? m[1] : 'amount';
    var parts = m ? fmt.split(m[0]) : ['', ''];
    return { kind: kind, prefix: parts[0] || '', suffix: parts[1] || '' };
  }
  var NUM = '(\\d(?:[\\d.,\\u00a0\\s]*\\d)?)';
  function buildRegex(fmtInfo) {
    var pre = fmtInfo.prefix, suf = fmtInfo.suffix;
    if (!pre.trim() && !suf.trim()) {
      /* format has no literal — fall back to symbol for base currency */
      var sym = SYMBOLS[BASE] || BASE;
      pre = sym; suf = '';
    }
    return new RegExp(escapeReg(pre) + '[\\s\\u00a0]*' + NUM + '[\\s\\u00a0]*' + escapeReg(suf), 'g');
  }
  var fmtMain = parseFormat(CFG.moneyFormat || '${{amount}}');
  var fmtWith = parseFormat(CFG.moneyWithCurrencyFormat || '');
  var regexes = [];
  if (CFG.moneyWithCurrencyFormat && (fmtWith.prefix.trim() || fmtWith.suffix.trim())) {
    regexes.push({ re: buildRegex(fmtWith), kind: fmtWith.kind });
  }
  regexes.push({ re: buildRegex(fmtMain), kind: fmtMain.kind });

  function parseAmount(str, kind) {
    var s = String(str).replace(/[\s ]/g, '');
    if (kind.indexOf('comma_separator') !== -1) {
      s = s.replace(/\./g, '').replace(/,/g, '.');
    } else {
      s = s.replace(/,/g, '');
    }
    var n = parseFloat(s);
    return isNaN(n) ? null : n;
  }

  /* ---------- formatting ---------- */
  function formatConverted(amount, currency) {
    var opts = { style: 'currency', currency: currency };
    if (Math.abs(amount) >= 50) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
    try { opts.currencyDisplay = 'narrowSymbol'; return new Intl.NumberFormat(LOCALES[currency] || 'en', opts).format(amount); }
    catch (e) {
      delete opts.currencyDisplay;
      try { return new Intl.NumberFormat(LOCALES[currency] || 'en', opts).format(amount); }
      catch (e2) { return (SYMBOLS[currency] || currency + ' ') + Math.round(amount); }
    }
  }

  /* ---------- rates ---------- */
  function loadCachedRates() {
    try {
      var raw = localStorage.getItem(RATES_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data.base !== BASE || (Date.now() - data.ts) > RATES_TTL) return null;
      return data.rates;
    } catch (e) { return null; }
  }
  function saveRates(rates) {
    try { localStorage.setItem(RATES_KEY, JSON.stringify({ base: BASE, ts: Date.now(), rates: rates })); } catch (e) {}
  }
  /* Age of the stored rates, for the background-refresh decision at the bottom
     of this file. loadCachedRates() deliberately returns only the rates, so the
     timestamp is read again here. Infinity when there is nothing usable, which
     reads as stale and refreshes. */
  function cachedRatesAge() {
    try {
      var raw = localStorage.getItem(RATES_KEY);
      if (!raw) return Infinity;
      var data = JSON.parse(raw);
      if (data.base !== BASE || typeof data.ts !== 'number') return Infinity;
      return Date.now() - data.ts;
    } catch (e) { return Infinity; }
  }
  function normalizeFawaz(json) {
    var key = BASE.toLowerCase();
    var obj = json && json[key];
    if (!obj) return null;
    var rates = {};
    LIST.forEach(function (c) {
      var r = obj[c.toLowerCase()];
      if (typeof r === 'number' && r > 0) rates[c] = r;
    });
    rates[BASE] = 1;
    return Object.keys(rates).length > 1 ? rates : null;
  }
  function normalizeErApi(json) {
    if (!json || json.result !== 'success' || !json.rates) return null;
    var rates = {};
    LIST.forEach(function (c) {
      var r = json.rates[c];
      if (typeof r === 'number' && r > 0) rates[c] = r;
    });
    rates[BASE] = 1;
    return Object.keys(rates).length > 1 ? rates : null;
  }
  function fetchJson(url) {
    return fetch(url, { cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    });
  }
  function fetchRates() {
    var b = BASE.toLowerCase();
    return fetchJson('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/' + b + '.min.json')
      .then(normalizeFawaz)
      .then(function (r) { if (!r) throw new Error('bad payload'); return r; })
      .catch(function () {
        return fetchJson('https://latest.currency-api.pages.dev/v1/currencies/' + b + '.min.json')
          .then(normalizeFawaz)
          .then(function (r) { if (!r) throw new Error('bad payload'); return r; });
      })
      .catch(function () {
        return fetchJson('https://open.er-api.com/v6/latest/' + BASE)
          .then(normalizeErApi)
          .then(function (r) { if (!r) throw new Error('bad payload'); return r; });
      });
  }

  /* ---------- text-node engine ---------- */
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, TEXTAREA: 1, INPUT: 1, 'MIO-CURRENCY-SELECTOR': 1 };
  function skippable(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      if (SKIP_TAGS[n.nodeName]) return true;
      if (n.hasAttribute && n.hasAttribute('data-mio-no-convert')) return true;
    }
    return false;
  }

  function convertNode(node, rate, currency) {
    var text = state.records.has(node) ? state.records.get(node).orig : node.nodeValue;
    if (!text || !/\d/.test(text)) return;
    var out = text, changed = false;
    for (var i = 0; i < regexes.length; i++) {
      var kind = regexes[i].kind;
      regexes[i].re.lastIndex = 0;
      out = out.replace(regexes[i].re, function (full, amt) {
        var val = parseAmount(amt, kind);
        if (val == null) return full;
        changed = true;
        return formatConverted(val * rate, currency);
      });
      if (changed) break; /* with-currency format matched; don't double-convert */
    }
    if (!changed) return;
    if (!state.records.has(node)) {
      state.records.set(node, { orig: text, out: out });
      state.tracked.push(node);
    } else {
      state.records.get(node).out = out;
    }
    if (node.nodeValue !== out) node.nodeValue = out;
  }

  function walk(root, rate, currency) {
    if (!root) return;
    if (root.nodeType === 3) {
      if (!skippable(root.parentElement)) convertNode(root, rate, currency);
      return;
    }
    if (root.nodeType !== 1 && root.nodeType !== 11) return;
    if (root.nodeType === 1 && skippable(root)) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue || !/\d/.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
        if (skippable(n.parentElement)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (n) { convertNode(n, rate, currency); });
  }

  function restoreAll() {
    applying = true;
    state.tracked.forEach(function (node) {
      var rec = state.records.get(node);
      if (rec && node.nodeValue === rec.out) node.nodeValue = rec.orig;
      state.records.delete(node);
    });
    state.tracked = [];
    applying = false;
  }

  function applyAll() {
    if (state.active === BASE || !state.rates) return;
    var rate = state.rates[state.active];
    if (!rate) return;
    applying = true;
    walk(document.body, rate, state.active);
    applying = false;
  }

  /* ---------- observer ---------- */
  var pending = new Set();
  var flushTimer = null;
  function flush() {
    flushTimer = null;
    if (state.active === BASE || !state.rates) { pending.clear(); return; }
    var rate = state.rates[state.active];
    if (!rate) { pending.clear(); return; }
    applying = true;
    pending.forEach(function (n) {
      if (!n.isConnected) return;
      walk(n, rate, state.active);
    });
    pending.clear();
    syncUi(); /* re-rendered fragments (AJAX cart, header) need labels/notes again */
    applying = false;
  }
  function schedule(node) {
    pending.add(node);
    if (!flushTimer) flushTimer = setTimeout(flush, 40);
  }

  var observer = new MutationObserver(function (muts) {
    if (applying || state.active === BASE) return;
    for (var i = 0; i < muts.length; i++) {
      var m = muts[i];
      if (m.type === 'characterData') {
        var rec = state.records.get(m.target);
        if (rec && m.target.nodeValue === rec.out) continue; /* our own write */
        if (rec) { state.records.delete(m.target); }
        schedule(m.target);
      } else if (m.type === 'childList') {
        for (var j = 0; j < m.addedNodes.length; j++) schedule(m.addedNodes[j]);
      }
    }
  });

  /* ---------- notes + selector label sync ---------- */
  function syncUi() {
    var isBase = state.active === BASE;
    /* MutationObserver delivers after applying is reset. Replacing unchanged
       text here creates childList records that schedule another flush forever. */
    document.querySelectorAll('[data-mio-currency-note]').forEach(function (el) {
      var tpl = el.getAttribute('data-note-template') || 'Prices in [display] are estimates. You will be charged in [base] at checkout.';
      var text = tpl.replace('[display]', state.active).replace('[base]', BASE);
      if (el.textContent !== text) el.textContent = text;
      if (el.hidden !== isBase) el.hidden = isBase;
    });
    document.querySelectorAll('[data-mio-cur-label]').forEach(function (el) {
      var sym = SYMBOLS[state.active] || '';
      var text = state.active + (sym && sym !== state.active ? ' ' + sym : '');
      if (el.textContent !== text) el.textContent = text;
    });
    document.querySelectorAll('[data-mio-cur-name]').forEach(function (el) {
      var c = el.getAttribute('data-mio-cur-name');
      if (!el.textContent) el.textContent = NAMES[c] || '';
    });
    document.querySelectorAll('[data-mio-cur-option]').forEach(function (el) {
      el.classList.toggle('mio-cur-active', el.getAttribute('data-mio-cur-option') === state.active);
    });
  }

  /* ---------- public api ---------- */
  var MioCurrency = {
    get base() { return BASE; },
    get active() { return state.active; },
    get list() { return LIST.slice(); },
    name: function (c) { return NAMES[c] || c; },
    symbol: function (c) { return SYMBOLS[c] || c; },
    rate: function (c) { return state.rates ? state.rates[c || state.active] : null; },

    set: function (currency, opts) {
      currency = String(currency || '').toUpperCase();
      if (LIST.indexOf(currency) === -1) return;
      if (!(opts && opts.silent)) {
        try { localStorage.setItem(PREF_KEY, currency); } catch (e) {}
      }
      if (currency === state.active) { syncUi(); return; }
      restoreAll();
      state.active = currency;
      applyAll();
      syncUi();
      document.dispatchEvent(new CustomEvent('mio:currency:change', {
        detail: { currency: state.active, base: BASE, rate: this.rate() }
      }));
    }
  };
  window.MioCurrency = MioCurrency;

  /* ---------- boot ---------- */
  function start() {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });

    var saved = null;
    try { saved = localStorage.getItem(PREF_KEY); } catch (e) {}
    if (saved && LIST.indexOf(saved.toUpperCase()) !== -1) {
      MioCurrency.set(saved.toUpperCase(), { silent: true });
    } else if (AUTO) {
      detectCountry().then(function (cc) {
        var cur = countryToCurrency(cc);
        if (cur && cur !== state.active) MioCurrency.set(cur, { silent: true });
      });
    }
    syncUi();
  }

  function detectCountry() {
    var cached = null;
    try { cached = sessionStorage.getItem(GEO_KEY); } catch (e) {}
    if (cached) return Promise.resolve(cached);
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    if (ctl) setTimeout(function () { ctl.abort(); }, 4000);
    return fetch('/browsing_context_suggestions.json', ctl ? { signal: ctl.signal } : {})
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var cc = json && json.detected_values && json.detected_values.country &&
                 (json.detected_values.country.handle || json.detected_values.country.iso_code);
        if (!cc && json && json.detected_values && typeof json.detected_values.country_code === 'string') {
          cc = json.detected_values.country_code;
        }
        if (cc) { try { sessionStorage.setItem(GEO_KEY, cc); } catch (e) {} }
        return cc || null;
      })
      .catch(function () { return null; });
  }

  var cached = loadCachedRates();
  var ready;
  if (cached) {
    state.rates = cached;
    ready = Promise.resolve();
    /* Refresh in the background only once the cache is past half its life. The
       age check was missing, so every pageview hit the third-party rates CDN
       regardless — for data that moves once a day, against a cache that is
       valid for twelve hours. */
    if (cachedRatesAge() > RATES_TTL / 2) {
      fetchRates().then(saveRates).catch(function () {});
    }
  } else {
    ready = fetchRates().then(function (rates) {
      state.rates = rates;
      saveRates(rates);
    }).catch(function () { /* no rates -> stay in base currency */ });
  }

  ready.then(function () {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  });
})();
